import { supportedVideoFormats } from "@/shared/video";

import { type AssociationRecord } from "../association";

import {
  type MessagePathRecord,
  type MessagePathRepresentationMapping,
} from "./MessagePathRecord";

export enum RepresentationStorageFormat {
  MCAP = "mcap",
  PARQUET = "parquet",
}

export interface RepresentationRecord {
  association: AssociationRecord;
  context: Record<string, string>;
  /**
   * Creation timestamp (ISO 8601). Always present on the wire; optional here
   * because long-lived fixtures and persisted viz state may predate the field.
   * Used to prefer the newest representation per (format, transformations)
   * variant, mirroring the server-side dedup in
   * `Topic.get_message_path_representations`.
   */
  created?: string;
  /**
   * Content format descriptor for this representation.
   * For image topics: the image encoding (e.g. "jpeg", "png") for processed representations,
   * or the ROS schema name (e.g. "sensor_msgs/Image") for original representations.
   * Undefined for non-image topics or legacy representations.
   */
  format?: string;
  /** Last-modified timestamp (ISO 8601). See `created` for optionality. */
  modified?: string;
  representation_id: string;
  storage_format: RepresentationStorageFormat;
  /**
   * Ordered list of transformation descriptors applied to produce this representation.
   * Empty or undefined for original/passthrough representations.
   * Example: ["downsample:0.5", "encode:jpeg"]
   */
  transformations?: string[];
}

const SUPPORTED_IMAGE_FORMATS = new Set(["jpeg", "jpg", "png"]);

/**
 * Content formats the visualizer can decode as compressed video (via
 * WebCodecs). Unlike image formats, a frame in these formats is generally
 * not decodable on its own — decoding starts at the nearest earlier keyframe.
 *
 * Derived from the video codec registry, so a codec registered there is
 * selectable here without a matching edit. "compressedvideo" is the
 * codec-agnostic format ingestion registers for passthrough compressed-video
 * topics (e.g. foxglove CompressedVideo); the actual codec is declared per
 * message and verified at decode time.
 */
const SUPPORTED_VIDEO_FORMATS = new Set([
  ...supportedVideoFormats(),
  "compressedvideo",
]);

/**
 * Criteria for selecting among multiple representations of the same data.
 *
 * Mirrors the SDK's `roboto.domain.topics.RepresentationSelector`. The selector
 * is a *hard filter*: only matching representations qualify, and message paths
 * with no matching rep are dropped — callers must handle empty/partial output.
 *
 * **Legacy carve-out for `contentFormat`:** representations with no `format` set
 * (predating the field) match any `contentFormat` request. When both an explicit
 * format match and a legacy rep are available for the same path, the explicit
 * match wins.
 */
export interface RepresentationSelector {
  /**
   * Accept representations whose `format` matches the given value, or any of
   * the given values when a list. Comparison is case-insensitive. Reps without
   * a `format` field also qualify (legacy carve-out).
   *
   * `undefined` ⇒ no constraint.
   *
   * Passing `[]` is an error: omit the field instead.
   */
  contentFormat?: string | string[];
  /**
   * Accept representations whose `transformations` field matches exactly.
   *
   * `[]` matches reps with no transformations (raw data). `undefined` ⇒ no
   * constraint.
   */
  transformations?: string[];
}

function acceptedContentFormats(contentFormat: string | string[]): Set<string> {
  const list = Array.isArray(contentFormat) ? contentFormat : [contentFormat];
  if (list.length === 0) {
    throw new Error(
      "RepresentationSelector.contentFormat: empty list is not a valid filter; omit the field instead",
    );
  }
  return new Set(list.map((f) => f.toLowerCase()));
}

function selectorMatches(
  representation: RepresentationRecord,
  selector: RepresentationSelector,
): boolean {
  if (selector.contentFormat !== undefined) {
    const accepted = acceptedContentFormats(selector.contentFormat);
    if (typeof representation.format === "string") {
      if (!accepted.has(representation.format.toLowerCase())) {
        return false;
      }
    }
    // Legacy carve-out: reps without `format` set fall through and are accepted.
  }
  if (selector.transformations !== undefined) {
    const actual = representation.transformations ?? [];
    if (actual.length !== selector.transformations.length) {
      return false;
    }
    if (selector.transformations.some((t, i) => t !== actual[i])) {
      return false;
    }
  }
  return true;
}

/**
 * Select the best representation given an optional selector.
 *
 * - **With a selector**: only matching reps qualify. When `contentFormat` is set,
 *   reps with an explicit matching `format` beat legacy reps (no `format` set)
 *   under the legacy carve-out. Returns `null` if nothing matches.
 * - **Without a selector**: prefer a processed representation (non-empty
 *   `transformations`); fall back to the first available.
 */
export function selectRepresentation(
  representations: RepresentationRecord[],
  selector?: RepresentationSelector,
): RepresentationRecord | null {
  if (representations.length === 0) {
    return null;
  }

  if (selector) {
    if (selector.contentFormat !== undefined) {
      // Prefer explicit format match over legacy carve-out.
      const explicit = representations.find(
        (r) => typeof r.format === "string" && selectorMatches(r, selector),
      );
      if (explicit) {
        return explicit;
      }
    }
    return representations.find((r) => selectorMatches(r, selector)) ?? null;
  }

  const processed = representations.find(
    (r) => r.transformations && r.transformations.length > 0,
  );
  if (processed) {
    return processed;
  }

  return representations[0];
}

/**
 * Resolve each message path to its best file, then group by file.
 *
 * For each message path across all mappings, selects the best representation
 * using {@link selectRepresentation}. Results are keyed by file (association_id),
 * not representation_id, so a default representation and a message-path-specific
 * representation that point to the same file merge naturally.
 *
 * Returns one mapping per file, each listing the message paths that should be
 * read from that file.
 */
export function resolveMessagePathFiles(
  mappings: MessagePathRepresentationMapping[],
  selector?: RepresentationSelector,
): MessagePathRepresentationMapping[] {
  return resolvePerMessagePath(mappings, (representations) =>
    selectRepresentation(representations, selector),
  );
}

/**
 * Resolve each message path to its best *media* file for visualization.
 *
 * When `videoSupported`, a representation with an explicit format in
 * {@link SUPPORTED_VIDEO_FORMATS} wins; otherwise (or when none exists) the
 * path falls back to {@link SUPPORTED_IMAGE_FORMATS} selection, including
 * the legacy carve-out for representations without a `format`. A legacy
 * representation is never treated as video: the `format` field postdates all
 * legacy representations, and misreading a JPEG file as an H.264 stream would
 * fail at decode time rather than resolution time.
 *
 * Message paths with no matching representation are dropped, like in
 * {@link resolveMessagePathFiles}. Callers distinguish video from image
 * results by the resolved representation's `format`.
 */
export function resolveMediaMessagePathFiles(
  mappings: MessagePathRepresentationMapping[],
  options: { videoSupported: boolean },
): MessagePathRepresentationMapping[] {
  const imageSelector: RepresentationSelector = {
    contentFormat: [...SUPPORTED_IMAGE_FORMATS],
  };
  return resolvePerMessagePath(mappings, (representations) => {
    if (options.videoSupported) {
      const video = representations.find(
        (representation) =>
          typeof representation.format === "string" &&
          SUPPORTED_VIDEO_FORMATS.has(representation.format.toLowerCase()),
      );
      if (video) {
        return video;
      }
    }
    return selectRepresentation(representations, imageSelector);
  });
}

/**
 * Shared plumbing for the resolve* functions: invert mappings to candidate
 * representations per message path, pick one via `choose`, then group the
 * chosen paths by file (association_id) so a default representation and a
 * message-path-specific representation pointing at the same file merge.
 */
function resolvePerMessagePath(
  mappings: MessagePathRepresentationMapping[],
  choose: (
    representations: RepresentationRecord[],
  ) => RepresentationRecord | null,
): MessagePathRepresentationMapping[] {
  // 1. Invert: for each message path, collect all candidate representations
  const mpCandidates = new Map<
    string,
    { record: MessagePathRecord; representations: RepresentationRecord[] }
  >();
  for (const mapping of mappings) {
    for (const mp of mapping.message_paths) {
      let entry = mpCandidates.get(mp.message_path_id);
      if (!entry) {
        entry = { record: mp, representations: [] };
        mpCandidates.set(mp.message_path_id, entry);
      }
      entry.representations.push(mapping.representation);
    }
  }

  // 2. Select best representation per message path, group by file
  const fileGroups = new Map<
    string,
    { representation: RepresentationRecord; messagePaths: MessagePathRecord[] }
  >();
  for (const { record, representations } of mpCandidates.values()) {
    const selected = choose(representations);
    if (!selected) {
      continue;
    }
    const fileId = selected.association.association_id;
    let group = fileGroups.get(fileId);
    if (!group) {
      group = { representation: selected, messagePaths: [] };
      fileGroups.set(fileId, group);
    }
    group.messagePaths.push(record);
  }

  return Array.from(fileGroups.values()).map(
    ({ representation, messagePaths }) => ({
      representation,
      message_paths: messagePaths,
    }),
  );
}

export { SUPPORTED_IMAGE_FORMATS, SUPPORTED_VIDEO_FORMATS };
